import { naturalCompare } from './reviewHelpers';

export interface PartnerVolumesData {
  partner_name: string;
  week_date: string;
  week_num: number;
  reserve_code: string;
  extracted: number;
  infiltrated: number;
  sprayed: number;
  accumulated: number;
  lost: number;
  replenished: number;
}

export interface CoordinatesData {
  name: string;
  lat: number;
  lon: number;
}

export type WeeklyTotal = Omit<
  PartnerVolumesData,
  'partner_name' | 'reserve_code'
>;

export interface ExecutiveReportModel {
  volumesData: PartnerVolumesData[];
  coordinatesData: CoordinatesData[];
  dataByReserve: Record<string, PartnerVolumesData[]>;
  sortedWeeklyTotals: WeeklyTotal[];
  uniqueReserves: string[];
  firstWeekDate?: string;
  lastWeekDate?: string;
}

const volumeFields = [
  'extracted',
  'infiltrated',
  'sprayed',
  'accumulated',
  'lost',
  'replenished',
] as const;

export const buildExecutiveReportModel = (
  volumes: PartnerVolumesData[],
  coordinates: CoordinatesData[]
): ExecutiveReportModel => {
  const volumesData = [...volumes].sort(
    (left, right) =>
      String(left.week_date).localeCompare(String(right.week_date))
      || naturalCompare(left.reserve_code, right.reserve_code)
  );
  const dataByReserve: Record<string, PartnerVolumesData[]> = {};
  const weeklyTotals: Record<string, WeeklyTotal> = {};

  volumesData.forEach((row) => {
    if (!dataByReserve[row.reserve_code]) dataByReserve[row.reserve_code] = [];
    dataByReserve[row.reserve_code].push(row);

    const weekKey = `${row.week_num}-${row.week_date}`;
    if (!weeklyTotals[weekKey]) {
      weeklyTotals[weekKey] = {
        week_num: row.week_num,
        week_date: row.week_date,
        extracted: 0,
        infiltrated: 0,
        sprayed: 0,
        accumulated: 0,
        lost: 0,
        replenished: 0,
      };
    }
    volumeFields.forEach((field) => {
      weeklyTotals[weekKey][field] += Number(row[field] || 0);
    });
  });

  return {
    volumesData,
    coordinatesData: [...coordinates],
    dataByReserve,
    sortedWeeklyTotals: Object.values(weeklyTotals).sort(
      (left, right) =>
        String(left.week_date).localeCompare(String(right.week_date))
        || left.week_num - right.week_num
    ),
    uniqueReserves: Object.keys(dataByReserve).sort(naturalCompare),
    firstWeekDate: volumesData[0]?.week_date,
    lastWeekDate: volumesData[volumesData.length - 1]?.week_date,
  };
};
